import { blogClient } from 'apollo/client';
import Error404 from 'components/desktop/error-404';
import MobileError404 from 'components/mobile/error-404';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  MAIN_DOMAIN,
  SITE_MAP,
} from 'globalConstants';
import {
  ARTICLE,
  ARTICLES,
  ArticleData,
  ArticleEntity,
  ArticleEntityResponseCollection,
  ArticleRelationResponseCollection,
  ArticleVariables,
  ArticlesData,
  ArticlesVariables,
  CATEGORY,
  CategoryData,
  CategoryEntity,
  CategoryVariables,
  MOST_VIEWED_ARTICLE,
  MostViewedArticleData,
  MostViewedArticleVariables,
  PublicationState,
} from 'graphql/blog/queries';
import { Omit, first, includes, isEqual, toString } from 'lodash';
import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Context, Fragment, createContext } from 'react';
import { getArticleJsonLD, getBreadcrumbListJsonLD } from 'seo/schema';
import { detectDeviceByUserAgent } from 'utils';

const MobileListArticle = dynamic(() => import('components/mobile/list-article'));
const ListArticle = dynamic(() => import('components/desktop/list-article'));
const MobileDetailArticle = dynamic(() => import('components/mobile/detail-article'));
const DetailArticle = dynamic(() => import('components/desktop/detail-article'));

const DEFAULT_LIMIT_BY_CATEGORY_CARD_ITEM = 7;
const DEFAULT_LIMIT_RELATED_CARD_ITEM = 6;
const DEFAULT_LIMIT_NEWEST_CARD_ITEM = 7;

const categorySlugs = [
  'kien-thuc-bat-dong-san',
  'thong-tin-thi-truong',
  'phong-thuy-nha-o',
  'phong-thuy-nha-dat',
  'tu-van-dau-tu-bat-dong-san',
  'phong-thuy-theo-tuoi',
  'vay-von-mua-bat-dong-san',
  'thu-tuc-phap-ly-bat-dong-san',
  'cac-loai-bat-dong-san',
  'so-sanh-cac-loai-bat-dong-san',
  'tin-tuc-bds',
  'proptech-viet-nam',
  'phong-thuy-phong-ngu',
  'phong-thuy-phong-khach',
  'phong-thuy-phong-tam',
  'phong-thuy-phong-bep',
  'phong-thuy-cua-chinh-san-vuon',
  'phong-thuy-vi-tri',
  'phong-thuy-hinh-dang-dat',
  'so-sanh-dau-tu-bds-va-cac-kenh-khac',
  'vi-tri-bat-dong-san',
  'loai-hinh-bds-dau-tu',
  'ty',
  'suu',
  'dan',
  'mao',
  'thin',
  'ty-1',
  'ngo',
  'mui',
  'than',
  'dau',
  'tuat',
  'hoi',
  'kiem-tra-quy-hoach',
  'luu-y-khi-mua-ban-bds',
];

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req } = context;
  const { headers } = req;
  const device = detectDeviceByUserAgent(headers['user-agent']);
  let pageData = null;
  const { [SITE_MAP.BLOG.LIST_OR_DETAIL_ARTICLE]: listOrDetailArticlePathname } =
    context.query ?? {};
  const isTypeList = includes(categorySlugs, listOrDetailArticlePathname);

  if (isTypeList) {
    const { data: categoryData } = await blogClient.query<CategoryData, CategoryVariables>({
      query: CATEGORY,
      variables: {
        slug: toString(listOrDetailArticlePathname),
      },
    });
    const { data: category } = categoryData.category ?? {};

    if (category) {
      const [{ data: articlesData }, { data: mostViewedArticleData }] = await Promise.all([
        blogClient.query<ArticlesData, ArticlesVariables>({
          query: ARTICLES,
          variables: {
            publicationState: PublicationState.LIVE,
            pagination: {
              pageSize: DEFAULT_LIMIT_BY_CATEGORY_CARD_ITEM,
            },
            filters: {
              categories: {
                slug: {
                  eq: toString(listOrDetailArticlePathname),
                },
              },
            },
            sort: ['publishedAt:desc'],
          },
        }),
        blogClient.query<MostViewedArticleData, MostViewedArticleVariables>({
          query: MOST_VIEWED_ARTICLE,
          variables: {
            publicationState: PublicationState.LIVE,
          },
        }),
      ]);
      const { articles: byCategoryArticlesWithPagination } = articlesData;
      const { articles: mostViewedArticlesWithPagination } =
        mostViewedArticleData.mostViewedArticle?.data?.attributes ?? {};

      pageData = {
        category: category ?? null,
        byCategoryArticlesWithPagination: byCategoryArticlesWithPagination ?? null,
        mostViewedArticlesWithPagination: mostViewedArticlesWithPagination ?? null,
      };
    }
  } else {
    const { data: articleData } = await blogClient.query<ArticleData, ArticleVariables>({
      query: ARTICLE,
      variables: {
        slug: toString(listOrDetailArticlePathname),
      },
    });
    const { data: article } = articleData.article ?? {};

    if (article) {
      const { slug: titleSlug, categories } = article?.attributes ?? {};
      const category = first(categories?.data);
      const { slug: categorySlug } = category?.attributes ?? {};
      const [{ data: articles1Data }, { data: articles2Data }] = await Promise.all([
        blogClient.query<ArticlesData, ArticlesVariables>({
          query: ARTICLES,
          variables: {
            publicationState: PublicationState.LIVE,
            pagination: {
              pageSize: DEFAULT_LIMIT_RELATED_CARD_ITEM,
            },
            filters: {
              slug: {
                not: {
                  eq: titleSlug,
                },
              },
              categories: {
                slug: {
                  eq: categorySlug,
                },
              },
            },
            sort: ['publishedAt:desc'],
          },
        }),
        blogClient.query<ArticlesData, ArticlesVariables>({
          query: ARTICLES,
          variables: {
            publicationState: PublicationState.LIVE,
            pagination: {
              pageSize: DEFAULT_LIMIT_NEWEST_CARD_ITEM,
            },
            sort: ['publishedAt:desc'],
          },
        }),
      ]);
      const { articles: relatedArticlesWithPagination } = articles1Data;
      const { articles: newestArticlesWithPagination } = articles2Data;

      pageData = {
        article,
        relatedArticlesWithPagination,
        newestArticlesWithPagination,
      };
    }
  }

  return {
    props: {
      device,
      isTypeList,
      pageData,
    },
  };
};

export const ListOrDetailArticlePageContext: Context<Omit<Props, 'device'>> = createContext({});

export interface ListArticlePageData {
  category?: CategoryEntity | null;
  byCategoryArticlesWithPagination?: ArticleEntityResponseCollection | null;
  mostViewedArticlesWithPagination?: ArticleRelationResponseCollection | null;
}
export interface DetailArticlePageData {
  article?: ArticleEntity | null;
  relatedArticlesWithPagination?: ArticleEntityResponseCollection | null;
  newestArticlesWithPagination?: ArticleEntityResponseCollection | null;
}
interface Props {
  device?: 'mobile' | 'desktop';
  isTypeList?: boolean;
  pageData?: ListArticlePageData | DetailArticlePageData | null;
}

const ListOrDetailArticlePage: NextPage = (props: Props) => {
  const { device, isTypeList, ...restProps } = props;
  const { pageData } = restProps;
  const router = useRouter();

  if (isTypeList) {
    const { category } = (pageData as ListArticlePageData) ?? {};
    const { title: categoryTitle, slug, featured_media } = category?.attributes ?? {};
    const { url: mediaUrl } = featured_media?.data?.attributes ?? {};
    const title = categoryTitle ? `${categoryTitle} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    const description = DEFAULT_DESCRIPTION;
    const ogUrl = `${MAIN_DOMAIN}/${SITE_MAP.BLOG.INDEX}/${slug}`;
    const ogImage = mediaUrl ?? DEFAULT_OG_IMAGE;

    const articlesBreadcrumbJsonLD = getBreadcrumbListJsonLD([
      {
        slug: SITE_MAP.BLOG.INDEX,
        tooltip: 'Tin tức',
      },
      {
        slug: `${SITE_MAP.BLOG.INDEX}/${slug}`,
        tooltip: categoryTitle,
      },
    ]);

    return (
      <Fragment>
        <Head>
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
          />
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
          />
          <meta name='description' content={description} />
          <meta name='og:title' content={title} />
          <meta name='og:description' content={description} />
          <meta name='og:url' content={ogUrl} />
          <meta name='og:image' content={ogImage} />
          <title>{title}</title>
        </Head>
        <ListOrDetailArticlePageContext.Provider value={restProps}>
          {isEqual(device, 'mobile') ? (
            category ? (
              <MobileListArticle />
            ) : (
              <MobileError404 />
            )
          ) : isEqual(device, 'desktop') ? (
            category ? (
              <ListArticle />
            ) : (
              <Error404 />
            )
          ) : null}
        </ListOrDetailArticlePageContext.Provider>
        <script
          key='articles-json-ld'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: articlesBreadcrumbJsonLD }}
        />
      </Fragment>
    );
  } else {
    const { article } = (pageData as DetailArticlePageData) ?? {};
    const { title: articleTitle, excerpt, featured_media } = article?.attributes ?? {};
    const { url: mediaUrl } = featured_media?.data?.attributes ?? {};
    const { [SITE_MAP.BLOG.LIST_OR_DETAIL_ARTICLE]: listArticlePathname } = router.query;
    const title = articleTitle ? `${articleTitle} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    const description = excerpt ?? DEFAULT_DESCRIPTION;
    const ogUrl = `${MAIN_DOMAIN}/${SITE_MAP.BLOG.INDEX}/${listArticlePathname}`;
    const ogImage = mediaUrl ?? DEFAULT_OG_IMAGE;
    const articleJson = getArticleJsonLD(article?.attributes);

    return (
      <Fragment>
        <Head>
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
          />
          <meta name='description' content={description} />
          <meta name='og:title' content={title} />
          <meta name='og:description' content={description} />
          <meta name='og:url' content={ogUrl} />
          <meta name='og:image' content={ogImage} />
          <title>{title}</title>
        </Head>
        <ListOrDetailArticlePageContext.Provider value={restProps}>
          {isEqual(device, 'mobile') ? (
            article ? (
              <MobileDetailArticle />
            ) : (
              <MobileError404 />
            )
          ) : isEqual(device, 'desktop') ? (
            article ? (
              <DetailArticle />
            ) : (
              <Error404 />
            )
          ) : null}
        </ListOrDetailArticlePageContext.Provider>
        <script
          key='news-article-json-ld'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: articleJson }}
        />
      </Fragment>
    );
  }
};

export default ListOrDetailArticlePage;
